<script lang="ts">
  import { updateMeta, injectJsonLd } from "../seo/meta";
  import { ROUTE_SEO } from "../seo/routes";
  import { ORGANIZATION_SCHEMA, combineSchemas, getBreadcrumbSchema } from "../seo/schemas";
  updateMeta(ROUTE_SEO["/"]);
  injectJsonLd(combineSchemas(ORGANIZATION_SCHEMA));
  import { route } from "@mateothegreat/svelte5-router";
  import {
    Zap,
    Shield,
    Clock,
    Code,
    Briefcase,
    Wrench,
    Play,
    ArrowRight,
    Activity,
    ChevronDown,
  } from "lucide-svelte";
  import FakeChart from "../components/FakeChart.svelte";
</script>

<div
  class="min-h-screen bg-black text-gray-300 selection:bg-orange-900 selection:text-white"
>
  <!-- SECTION 1 — HERO -->
  <section class="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
    <!-- Fake chart background -->
    <div class="absolute inset-0 opacity-[0.12] pointer-events-none select-none">
      <FakeChart />
    </div>
    <!-- Grid overlay -->
    <div
      class="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:48px_48px]"
    ></div>

    <div class="relative z-10 text-center px-4 max-w-4xl mx-auto">
      <!-- Status badge -->
      <div class="inline-flex items-center gap-2 mb-8">
        <span class="relative flex h-2 w-2">
          <span
            class="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-500 opacity-75"
          ></span>
          <span class="relative inline-flex rounded-full h-2 w-2 bg-orange-500"
          ></span>
        </span>
        <span
          class="badge badge-outline rounded-none uppercase tracking-[0.25em] font-mono text-[10px] text-orange-500 border-orange-500/30 px-4 py-3"
        >
          Crypto Trading Infrastructure
        </span>
      </div>

      <!-- Main headline -->
      <h1
        class="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white tracking-tight leading-[0.9] mb-8"
      >
        We Build
        <br />
        <span class="text-orange-500">Trading Bots</span>
        <br />
        <span class="text-gray-500 text-[0.7em]">For a Living</span>
      </h1>

      <!-- Subtitle -->
      <p
        class="text-lg md:text-xl text-gray-500 max-w-xl mx-auto mb-12 leading-relaxed"
      >
        Real-time Solana data. Battle-tested strategies.<br class="hidden sm:block" />
        From SDK to deployment.
      </p>

      <!-- CTAs -->
      <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
        <a
          use:route
          href="/products/sailfish"
          class="group btn btn-lg bg-orange-600 hover:bg-orange-500 text-white border-none rounded-sm uppercase tracking-[0.2em] font-bold px-10 transition-colors"
        >
          Explore Products
          <ArrowRight
            class="size-4 ml-1 transition-transform group-hover:translate-x-1"
          />
        </a>
        <a
          href="https://calendly.com/0xconstantin/30min"
          target="_blank"
          class="btn btn-lg bg-transparent text-gray-400 hover:text-white border border-gray-700 hover:border-orange-500/50 rounded-sm uppercase tracking-[0.2em] font-bold px-10 transition-colors"
        >
          Book a Call
        </a>
      </div>
    </div>

    <!-- Scroll indicator -->
    <div
      class="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-700"
    >
      <span class="text-[9px] uppercase tracking-[0.3em] font-mono">Scroll</span>
      <ChevronDown class="size-4" />
    </div>
  </section>

  <!-- SECTION 2 — STATS BAR -->
  <section class="border-y border-gray-800/80 bg-gray-950/60">
    <div
      class="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-800/60"
    >
      <div class="py-10 md:py-14 px-8 text-center">
        <div class="flex items-center justify-center gap-2 mb-3">
          <Zap class="size-4 text-orange-500/60" />
          <span class="text-[10px] font-mono text-gray-500 uppercase tracking-[0.3em]">Avg. Daily Volume</span>
        </div>
        <p class="text-4xl md:text-5xl font-black text-white tracking-tight tabular-nums">
          $455<span class="text-orange-500">k</span>
        </p>
      </div>

      <div class="py-10 md:py-14 px-8 text-center">
        <div class="flex items-center justify-center gap-2 mb-3">
          <Shield class="size-4 text-orange-500/60" />
          <span class="text-[10px] font-mono text-gray-500 uppercase tracking-[0.3em]">Uptime</span>
        </div>
        <p class="text-4xl md:text-5xl font-black text-white tracking-tight tabular-nums">
          99.9<span class="text-orange-500">%</span>
        </p>
      </div>

      <div class="py-10 md:py-14 px-8 text-center">
        <div class="flex items-center justify-center gap-2 mb-3">
          <Clock class="size-4 text-orange-500/60" />
          <span class="text-[10px] font-mono text-gray-500 uppercase tracking-[0.3em]">Latency</span>
        </div>
        <p class="text-4xl md:text-5xl font-black text-white tracking-tight tabular-nums">
          20<span class="text-gray-600">–</span>50<span class="text-orange-500">ms</span>
        </p>
      </div>
    </div>
  </section>

  <!-- SECTION 3 — PRODUCT SHOWCASE -->
  <section class="py-28 px-4">
    <div class="max-w-5xl mx-auto">
      <h2 class="text-[11px] font-mono text-center text-gray-500 mb-16 uppercase tracking-[0.4em]">
        What We Offer
      </h2>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
        <a use:route href="/products/sailfish" class="group block bg-gray-950/40 border border-gray-800/60 rounded-sm p-8 pt-0 overflow-hidden transition-colors hover:border-orange-900/40">
          <div class="h-1 bg-orange-600 -mx-8 mb-8"></div>
          <div class="w-10 h-10 rounded-sm bg-orange-600/10 border border-orange-600/20 flex items-center justify-center mb-5">
            <Code class="size-5 text-orange-500" />
          </div>
          <h3 class="text-lg font-bold text-white mb-2 tracking-tight">Sailfish SDK</h3>
          <p class="text-sm text-gray-400 leading-relaxed mb-6">
            Real-time Solana & Polymarket trading data. Streaming + historic API. Open source.
          </p>
          <div class="flex items-center justify-between">
            <span class="text-xs font-mono text-gray-500">From $75/mo</span>
            <span class="text-xs font-mono text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
              Learn more <ArrowRight class="size-3" />
            </span>
          </div>
        </a>

        <a use:route href="/services" class="group block bg-gray-950/40 border border-gray-800/60 rounded-sm p-8 pt-0 overflow-hidden transition-colors hover:border-blue-900/40">
          <div class="h-1 bg-blue-600 -mx-8 mb-8"></div>
          <div class="w-10 h-10 rounded-sm bg-blue-600/10 border border-blue-600/20 flex items-center justify-center mb-5">
            <Briefcase class="size-5 text-blue-500" />
          </div>
          <h3 class="text-lg font-bold text-white mb-2 tracking-tight">Services</h3>
          <p class="text-sm text-gray-400 leading-relaxed mb-6">
            Strategy review, backtesting, mentorship & co-development. Battle-tested expertise.
          </p>
          <div class="flex items-center justify-between">
            <span class="text-xs font-mono text-gray-500">From $499</span>
            <span class="text-xs font-mono text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
              Learn more <ArrowRight class="size-3" />
            </span>
          </div>
        </a>

        <a use:route href="/tools" class="group block bg-gray-950/40 border border-gray-800/60 rounded-sm p-8 pt-0 overflow-hidden transition-colors hover:border-emerald-900/40">
          <div class="h-1 bg-emerald-600 -mx-8 mb-8"></div>
          <div class="w-10 h-10 rounded-sm bg-emerald-600/10 border border-emerald-600/20 flex items-center justify-center mb-5">
            <Wrench class="size-5 text-emerald-500" />
          </div>
          <h3 class="text-lg font-bold text-white mb-2 tracking-tight">Tools</h3>
          <p class="text-sm text-gray-400 leading-relaxed mb-6">
            Free trading tools. Live charts, historic queries, orderbook streaming. Solana & Polymarket.
          </p>
          <div class="flex items-center justify-between">
            <span class="text-xs font-mono text-gray-500">Free</span>
            <span class="text-xs font-mono text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
              Explore <ArrowRight class="size-3" />
            </span>
          </div>
        </a>
      </div>
    </div>
  </section>

  <!-- SECTION 4 — LIVE DEMO TEASER -->
  <section class="border-y border-gray-800/80">
    <div class="max-w-4xl mx-auto py-24 px-4 text-center">
      <div class="flex justify-center mb-8">
        <div class="w-16 h-16 rounded-full bg-gray-900 border border-gray-800 flex items-center justify-center">
          <Activity class="size-7 text-orange-500" />
        </div>
      </div>

      <h2 class="text-[11px] font-mono text-gray-500 mb-6 uppercase tracking-[0.4em]">
        Live Infrastructure
      </h2>
      <h3 class="text-3xl md:text-4xl font-black text-white tracking-tight mb-4">
        See Our Data Pipeline in Action
      </h3>
      <p class="text-gray-400 max-w-lg mx-auto mb-10 leading-relaxed">
        Watch real trades, pool initializations, and token events stream in
        real-time from Solana and Polymarket.
      </p>

      <a
        use:route
        href="/products/sailfish"
        class="group inline-flex items-center gap-3 btn btn-lg bg-orange-600 hover:bg-orange-500 text-white border-none rounded-sm uppercase tracking-[0.2em] font-bold px-10 transition-colors"
      >
        <Play class="size-5 fill-current" />
        Launch Live Demo
      </a>
    </div>
  </section>

  <!-- SECTION 5 — ABOUT / ENTITY DEFINITION (SEO) -->
  <section class="border-t border-gray-800/80 py-20 px-4">
    <div class="max-w-3xl mx-auto">
      <h2 class="text-2xl font-bold text-white mb-6 tracking-tight">What is 0xfire?</h2>
      <div class="space-y-4 text-sm text-gray-400 leading-relaxed">
        <p>
          0xfire is a crypto trading infrastructure company founded by <a href="https://x.com/0xconstantin" target="_blank" class="text-orange-500 underline decoration-orange-500/30 hover:decoration-orange-500">0xconstantin</a>. The team builds and operates algorithmic trading systems across Solana, Ethereum, Binance Chain, Hyperliquid, Polymarket, and Kalshi.
        </p>
        <p>
          The company's flagship product is <a use:route href="/products/sailfish" class="text-orange-500 underline decoration-orange-500/30 hover:decoration-orange-500">Sailfish</a>, a real-time trading data API that streams live trades, pool initializations, token events, and orderbooks via WebSocket and REST API. Sailfish supports Raydium (AMM, CPMM, CLMM, LaunchLab), PumpFun, PumpSwap, and Meteora (DYN, DYN2, DLMM) on Solana, with an open-source TypeScript SDK.
        </p>
        <p>
          Beyond data infrastructure, 0xfire offers <a use:route href="/services" class="text-orange-500 underline decoration-orange-500/30 hover:decoration-orange-500">trading bot development services</a> ranging from strategy review and audit to full strategy co-development, as well as <a use:route href="/tools" class="text-orange-500 underline decoration-orange-500/30 hover:decoration-orange-500">free DeFi tools</a> including a live Solana trading dashboard, historic trade queries, pool graduations tracker, and a SOL wrapper utility.
        </p>
        <p>
          The team processes $455k in average daily trading volume with 99.9% uptime and 20-50ms latency across its production infrastructure.
        </p>
      </div>
    </div>
  </section>

  <!-- SECTION 6 — BOTTOM CTA -->
  <section class="py-32 px-4 bg-gray-950">
    <div class="text-center max-w-2xl mx-auto">
      <h2 class="text-[11px] font-mono text-gray-500 mb-6 uppercase tracking-[0.4em]">
        Get Started
      </h2>
      <h3 class="text-4xl md:text-5xl font-black text-white tracking-tight mb-6">
        Ready to Build
        <span class="text-orange-500">Your Edge</span>?
      </h3>
      <p class="text-gray-400 mb-12 leading-relaxed">
        Whether you need data infrastructure, strategy validation, or a full
        engineering team — we have you covered.
      </p>

      <a
        href="https://calendly.com/0xconstantin/30min"
        target="_blank"
        class="group inline-flex items-center gap-2 btn btn-lg bg-orange-600 hover:bg-orange-500 text-white border-none rounded-sm uppercase tracking-[0.2em] font-bold px-12 transition-colors"
      >
        Book a Free Call
        <ArrowRight
          class="size-4 transition-transform group-hover:translate-x-1"
        />
      </a>

      <p class="text-xs text-gray-500 mt-6 font-mono">
        15-minute intro call &middot; No commitment
      </p>
    </div>
  </section>
</div>
